<template>
  <AppShell
    v-if="!loadingRecord && isSubmitted && loadedRecord"
    role-label="Student"
    :nav-items="navItems"
    active-view="task"
    :account="auth.user || {}"
    :collapsed="navCollapsed"
    @navigate="openNav"
    @logout="signOut"
    @toggle-nav="navCollapsed = !navCollapsed"
  >
    <div class="record-workspace">
      <WritingRecordDetail
        :record="loadedRecord"
        mode="student"
        @back="handleBack"
      />
    </div>
  </AppShell>

  <div v-else class="page" :class="{ 'section-panel-open': activeRoundId }" :style="{ '--section-panel-width': `${improvePanelWidth}px` }">
    <p v-if="loadingRecord" role="status">Loading writing workspace…</p>
    <div v-if="workspaceError" class="ui-notice" role="alert">{{ workspaceError }} <button class="ui-button" @click="loadRecord">Reload saved draft</button></div>
    <div v-if="experimentParticipant" class="ui-notice" role="status">{{ experimentParticipant.mode === 'preview' ? 'PREVIEW · ' : experimentParticipant.mode === 'uat' ? 'API TEST · ' : '' }}Writing study · {{ experimentParticipant.status }} · Official grades are hidden until the post-test is complete.</div>
    <div v-if="!isSubmitted" class="page-header">
      <h1>Mini-Research Proposal</h1>
    </div>

    <NotionWorkspaceView
      v-if="!loadingRecord && !isSubmitted"
      :experiment="experimentParticipant"
      :experiment-busy="experimentStore.busy || saveLoading"
      :section-panel-open="Boolean(activeRoundId)"
      :ai-unavailable="aiUnavailable"
      :score-loading="scoreLoading"
      :save-loading="saveLoading"
      :save-status="saveStatus"
      :submit-loading="submitLoading"
      :readonly="isSubmitted || submitLoading || !capabilities.canEdit"
      @section-improve="startSectionRound"
      @back="handleBack"
      @score="handleSubmit"
      @final-submit="handleFinalSubmit"
      @save="handleSave"
      @topic-input="pendingTopic = $event"
    />
    <SectionImprovePanel v-if="activeRoundId && experimentParticipant" :round-id="activeRoundId" :dirty="experimentalDirty" :width="improvePanelWidth" @resize="improvePanelWidth = $event" @close="activeRoundId = null" />

    <div v-if="showBackModal" class="modal-overlay" @click.self="showBackModal = false">
      <div class="modal compact-modal">
        <div class="modal-header">
          <h2>Save changes before leaving?</h2>
          <button type="button" class="btn-icon" aria-label="Close leave dialog" @click="showBackModal = false">×</button>
        </div>
        <div class="modal-body">
          <p class="modal-copy">Your latest writing and chat changes have not been saved yet.</p>
        </div>
        <div class="modal-footer modal-actions">
          <button class="btn-secondary" @click="leaveWithoutSaving">Leave without saving</button>
          <button class="btn-primary" :disabled="saveLoading" @click="saveAndLeave">
            {{ saveLoading ? 'Saving...' : 'Save and leave' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showScoreModal && (store.scoreResult || scoreError)" class="modal-overlay" @click.self="closeScoreModal">
      <div class="modal">
        <div class="modal-header">
          <h2 v-if="scoreError">Scoring unavailable</h2>
          <h2 v-else>Your Score: {{ store.scoreResult.score }} / 4</h2>
          <button type="button" class="btn-icon" aria-label="Close score dialog" @click="closeScoreModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="scoreError" class="score-error" role="status">
            {{ scoreError }}
          </div>
          <template v-else>
            <p class="score-disclaimer">AI-generated scores and feedback are for reference only. The score given by the module teacher is final.</p>
            <div v-for="dim in store.scoreResult.dimensions" :key="dim.name" class="score-dimension">
              <div class="score-dim-name">{{ dim.name }}</div>
              <div class="score-dim-comment">{{ dim.comment }}</div>
            </div>
            <div class="score-divider" />
            <div class="score-summary">{{ store.scoreResult.summary }}</div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="closeScoreModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { apiFetch } from '../api'
import { readAiJson } from '../aiResponse'
import { useEssayStore } from '../stores/essay'
import { useAuthStore } from '../stores/auth'
import { buildScoreRequest } from '../utils/scoreRequest.mjs'
import AppShell from '../components/AppShell.vue'
import NotionWorkspaceView from './NotionWorkspaceView.vue'
import WritingRecordDetail from '../components/WritingRecordDetail.vue'
import { useExperimentStore } from '../experiments/store'
import { experimentApi } from '../experiments/api'
import { workspaceCapabilities, experimentDraft } from '../experiments/workspace-policy.mjs'
import SectionImprovePanel from '../experiments/SectionImprovePanel.vue'

const store = useEssayStore()
const experimentStore = useExperimentStore()
const experimentParticipant = ref(null), activeRoundId = ref(null), loadingRecord = ref(true), workspaceError = ref(''), savedExperimentDraft = ref('')
const capabilities = computed(() => workspaceCapabilities(experimentParticipant.value))
const experimentalDirty = computed(() => JSON.stringify(experimentDraft(store)) !== savedExperimentDraft.value)
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const scoreLoading = ref(false)
const saveLoading = ref(false)
const submitLoading = ref(false)
const showScoreModal = ref(false)
const showBackModal = ref(false)
const scoreError = ref('')
const aiUnavailable = ref(false)
const recordStatus = ref('not_started')
const loadedRecord = ref(null)
const mobileViewport = window.matchMedia('(max-width: 767px)')
const navCollapsed = ref(window.matchMedia('(max-width: 767px)').matches)
const savedSnapshot = ref('')
const pendingTopic = ref(''), allowLeave = ref(false), leaveTarget = ref('/student/task'), improvePanelWidth = ref(460), saveFailed = ref(false), savedOnce = ref(false)
const pendingTopicChange = computed(() => store.stage === 'topic' && pendingTopic.value.trim() !== store.researchTopic)
const saveStatus = computed(() => saveLoading.value ? 'Saving…' : saveFailed.value ? 'Save failed · changes remain in this browser' : pendingTopicChange.value ? 'Topic edits pending · click Start draft to keep them' : hasUnsavedChanges() ? 'Unsaved changes · save before leaving' : savedOnce.value ? 'Saved · manual save' : 'Manual save · changes are saved when you click Save draft')
const assignmentId = route.params.assignmentId
const writingVisitId = crypto.randomUUID()
const isSubmitted = computed(() => loadedRecord.value?.read_only || ['submitted', 'reviewed'].includes(recordStatus.value))
const navItems = [
  { view: 'task', label: 'Task', paths: ['M6 3h12v18H6z', 'M9 7h6', 'M9 12h6', 'M9 17h4', 'M5 7H3', 'M5 12H3', 'M5 17H3'] },
  { view: 'profile', label: 'Profile', paths: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 22a8 8 0 0 1 16 0'] },
]

onMounted(() => {
  mobileViewport.addEventListener('change', syncNavigationForViewport)
  window.addEventListener('beforeunload', guardBrowserLeave)
  loadRecord()
})

onBeforeUnmount(() => {
  mobileViewport.removeEventListener('change', syncNavigationForViewport)
  window.removeEventListener('beforeunload', guardBrowserLeave)
})

function guardBrowserLeave(event) {
  if (!loadingRecord.value && !isSubmitted.value && hasUnsavedChanges()) {
    event.preventDefault()
    event.returnValue = ''
  }
}
onBeforeRouteLeave(to => {
  if (allowLeave.value || loadingRecord.value || isSubmitted.value || !hasUnsavedChanges()) return true
  leaveTarget.value = to.fullPath
  showBackModal.value = true
  return false
})

function syncNavigationForViewport(event) {
  navCollapsed.value = event.matches
}

async function loadRecord() {
  loadingRecord.value = true
  workspaceError.value = ''
  try {
  const res = await apiFetch(`assignments/${assignmentId}/record`)
  if (!res.ok) {
    router.push('/student/task')
    return
  }
  const data = await res.json()
  if (data.record.experiment) {
    await experimentStore.loadParticipation(data.record.experiment.participant_id)
    experimentParticipant.value = experimentStore.participant
    if (['pre_questionnaire', 'pre_sample'].includes(experimentParticipant.value.stage)) {
      await router.replace(`/student/experiments/${experimentParticipant.value.id}`)
      return
    }
  } else experimentParticipant.value = null
  loadedRecord.value = data.record
  store.hydrateFromRecord(data.record)
  pendingTopic.value = store.researchTopic
  recordStatus.value = data.record.status
  savedSnapshot.value = workingSnapshot()
  savedExperimentDraft.value = JSON.stringify(experimentDraft(store))
  if (experimentParticipant.value) {
    experimentApi(`participants/${experimentParticipant.value.id}/events`, {action:'stage_view',stage:'writing',request_id:writingVisitId}).catch(() => {})
  }
  } catch(e) { workspaceError.value = e.message } finally { loadingRecord.value = false }
}

function workingSnapshot() {
  return JSON.parse(JSON.stringify({
    research_topic: store.researchTopic,
    cefr_level: store.cefrLevel,
    section_guidance: store.sectionGuidance,
    draft_sections: store.draftSections,
    references: store.references,
    score_result: store.scoreResult || {},
    messages: store.snapshotMessages(),
  }))
}

function hasUnsavedChanges() {
  return pendingTopicChange.value || JSON.stringify(workingSnapshot()) !== JSON.stringify(savedSnapshot.value)
}

function recordPayload(status) {
  return {
    status,
    ...workingSnapshot(),
  }
}

async function persistRecord(status) {
  const captured = workingSnapshot()
  if (experimentParticipant.value) {
    const payload = JSON.parse(JSON.stringify(experimentDraft(store)))
    await experimentStore.mutate(status === 'submitted' ? 'final-submit' : 'draft', { payload, revision: experimentStore.revision })
    await experimentStore.refresh()
    experimentParticipant.value = experimentStore.participant
    loadedRecord.value = experimentStore.record
    recordStatus.value = experimentStore.record.status
    savedExperimentDraft.value = JSON.stringify(payload)
    savedSnapshot.value = captured
    return experimentStore.record
  }
  const res = await apiFetch(`assignments/${assignmentId}/record`, {
    method: 'POST',
    body: JSON.stringify({ status, ...captured }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Could not save record.')
  if (JSON.stringify(workingSnapshot()) === JSON.stringify(captured)) store.hydrateFromRecord(data.record)
  loadedRecord.value = data.record
  recordStatus.value = data.record.status
  savedSnapshot.value = captured
  return data.record
}

async function handleSave() {
  if (pendingTopicChange.value) { workspaceError.value = 'Click Start draft to accept your topic edits before saving.'; return false }
  saveLoading.value = true
  saveFailed.value = false
  try {
    await persistRecord('in_progress')
    workspaceError.value = ''
    savedOnce.value = true
    return true
  } catch(e) {
    workspaceError.value = experimentStore.error || e.message
    saveFailed.value = true
    return false
  } finally {
    saveLoading.value = false
  }
}

async function runScore() {
  scoreLoading.value = true
  scoreError.value = ''
  try {
    const res = await apiFetch('score', {
      method: 'POST',
      body: JSON.stringify(buildScoreRequest({
        draft: store.combinedDraft,
        taskBrief: store.assignmentPrompt,
        researchTopic: store.researchTopic,
        references: store.references,
        cefrLevel: store.cefrLevel,
      })),
    })
    const data = await readAiJson(res, markAiUnavailable)
    if (!res.ok) throw new Error(data.error || 'Scoring failed.')
    store.scoreResult = data
    return true
  } catch {
    markAiUnavailable()
    scoreError.value = 'Scoring is temporarily unavailable. Your writing is unchanged, and final submission does not require an AI score. Please try Score again later.'
    return false
  } finally {
    scoreLoading.value = false
  }
}

function markAiUnavailable() {
  aiUnavailable.value = true
}

async function handleSubmit() {
  if (!capabilities.value.canUseLegacyAI) return
  const scored = await runScore()
  if (scored) await persistRecord('in_progress')
  showScoreModal.value = true
}

async function handleFinalSubmit() {
  if (experimentParticipant.value) {
    if (!window.confirm('Submit this exact final proposal and enter the post-test? Writing will be locked.')) return
    submitLoading.value = true
    try { await persistRecord('submitted'); await router.replace(`/student/experiments/${experimentParticipant.value.id}`) }
    catch(e) { workspaceError.value = experimentStore.error || e.message }
    finally { submitLoading.value = false }
    return
  }
  submitLoading.value = true
  try {
    if (!store.scoreResult) {
      await runScore()
    }
    await persistRecord('submitted')
    showScoreModal.value = true
  } finally {
    submitLoading.value = false
  }
}

async function startSectionRound(section) {
  if (!capabilities.value.canStartRound || experimentStore.busy || saveLoading.value) return
  saveLoading.value = true; workspaceError.value = ''
  try {
    await persistRecord('in_progress')
    const data = await experimentStore.mutate('rounds', {section, revision: experimentStore.revision})
    activeRoundId.value = data.round.id
  } catch(e) { workspaceError.value = experimentStore.error || e.message }
  finally { saveLoading.value = false }
}

function handleBack() {
  if (isSubmitted.value || !hasUnsavedChanges()) {
    router.push('/student/task')
    return
  }
  showBackModal.value = true
}

function openNav(view) {
  router.push({ path: '/student/task', query: { view } })
}

async function signOut() {
  await auth.logout()
  router.replace('/login')
}

async function saveAndLeave() {
  const saved = await handleSave()
  if (saved && !hasUnsavedChanges()) { allowLeave.value = true; router.push(leaveTarget.value) }
  else showBackModal.value = false
}

function leaveWithoutSaving() {
  allowLeave.value = true
  router.push(leaveTarget.value)
}

function closeScoreModal() {
  showScoreModal.value = false
}
</script>

<style scoped>
@media (min-width: 1000px) { .page.section-panel-open { width: calc(100% - var(--section-panel-width, 460px)); } }
.record-workspace { min-width: 0; }
.page { display: flex; flex-direction: column; width: 100%; height: 100vh; min-height: 100vh; overflow: hidden; background: var(--canvas); }
.page-header { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.modal-overlay { position: fixed; inset: 0; background: rgba(20, 20, 19, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #faf9f5; border: 1px solid #f0eee6; border-radius: 16px; box-shadow: 0 8px 40px rgba(0,0,0,0.18); width: 600px; max-width: 95vw; max-height: 85vh; display: flex; flex-direction: column; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
.modal-header h2 { font-size: 17px; font-weight: 700; color: #141413; font-family: Georgia, serif; margin: 0; }
.btn-icon { display: inline-grid; min-width: 36px; min-height: 36px; place-items: center; background: none; border: none; font-size: 18px; color: #5e5d59; padding: 4px 8px; border-radius: 6px; cursor: pointer; }
.btn-icon:hover { background: #e8e6dc; }
.modal-body { flex: 1; overflow-y: auto; padding: 16px 24px; }
.modal-footer { padding: 12px 24px 20px; display: flex; justify-content: flex-end; border-top: 1px solid #f0eee6; }
.compact-modal { width: 460px; }
.modal-copy { color: #4d4c48; font-size: 14px; line-height: 1.55; margin: 0; }
.modal-actions { gap: 8px; }
.btn-primary,
.btn-secondary { border: none; border-radius: 8px; padding: 9px 14px; cursor: pointer; font-size: 14px; }
.btn-primary { background: #c96442; color: #faf9f5; }
.btn-secondary { background: #e8e6dc; color: #4d4c48; }
.btn-primary:disabled,
.btn-secondary:disabled { opacity: 0.55; cursor: not-allowed; }
.score-dimension { margin-bottom: 16px; }
.score-dim-name { font-size: 13px; font-weight: 600; color: #141413; margin-bottom: 4px; }
.score-dim-comment { font-size: 13px; color: #5e5d59; line-height: 1.6; }
.score-divider { height: 1px; background: #f0eee6; margin: 16px 0; }
.score-summary { font-size: 13px; color: #4d4c48; line-height: 1.6; font-style: italic; }
.score-error { border-left: 3px solid #c96442; padding: 10px 12px; background: #f5ebe5; color: #4d4c48; font-size: 14px; line-height: 1.55; }
@supports (height: 100dvh) {
  .page { height: 100dvh; min-height: 100dvh; }
}
@media (max-width: 767px) {
  .btn-icon { min-width: 44px; min-height: 44px; }
}
</style>
